import React, { useState, useEffect, useMemo } from 'react';
import { useData } from '../../contexts/DataContext';
import SectionHeader from '../common/SectionHeader';
import ListSection from '../common/ListSection';
import ToolbarButton from '../common/ToolbarButton';
import ScrollableFilters from '../Rules/components/ScrollableFilters';
import { useScrollableFilters } from '../Rules/hooks/useScrollableFilters';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add as AddIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { DndProvider } from 'react-dnd';
import DragPreviewLayer from '../common/DragPreviewLayer';
import AppDialog, { DialogField } from '../common/AppDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import { HTML5Backend } from 'react-dnd-html5-backend';
import InfoBanner from '../common/InfoBanner';
import CategoryIcon from '@mui/icons-material/Category';
import useBudgetData from '../Budget/useBudgetData';
import CategoryAnalysisDialog from './CategoryAnalysisDialog';
import CategoryCard from './CategoryCard';
import { RANGE_OPTIONS, monthsForRange, monthKeysForRange } from '../../utils/entityTrendSeries';
import { FORECAST_METHODS } from '../../utils/categoryForecast';

// Same white-background filter fields as the Goals/Transactions toolbars.
const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#fff' },
    '&.Mui-focused': { backgroundColor: '#fff' }
  }
};

// Old AnalysisHeader replaced by reusable SectionHeader component

// Gray pill Tabs recipe shared by the card display controls under the filters.
const pillTabsSx = {
  minHeight: 0,
  '& .MuiTab-root': { py: 0.75, px: 1.25, minHeight: 0, minWidth: 0, fontSize: '0.8125rem', borderRadius: 5, color: 'text.secondary', textTransform: 'none' },
  '& .Mui-selected': { color: 'primary.main', bgcolor: 'white' },
  '& .MuiTabs-indicator': { display: 'none' },
};

const CategoryManager = () => {
  const { categories, budgets, addCategory, updateCategory, deleteCategory, transactions } = useData();
  // Per-category monthly income/expense maps — feeds the trends chart, every
  // card's spend series, and the AI analysis (computed once here, not per child).
  const budgetData = useBudgetData(transactions, categories);
  const [analyzeCategory, setAnalyzeCategory] = useState(null);

  // Chart window for every card, switched from the History pill next to the
  // Partial months toggle and remembered per page.
  const [range, setRange] = useState(() => {
    try {
      const saved = localStorage.getItem('categoriesTrendRange');
      return RANGE_OPTIONS.some((o) => o.value === saved) ? saved : '6M';
    } catch {
      return '6M';
    }
  });
  const changeRange = (value) => {
    setRange(value);
    try {
      localStorage.setItem('categoriesTrendRange', value);
    } catch {
      // persistence is best-effort
    }
  };
  const monthKeys = useMemo(() => monthKeysForRange(monthsForRange(range)), [range]);

  // Whether the current in-progress month counts toward each card's
  // "Avg / month" figure. Excluding it keeps a half-elapsed month from
  // dragging the average down. Remembered per page, like the range.
  const [partialMonths, setPartialMonths] = useState(() => {
    try {
      return localStorage.getItem('categoriesPartialMonths') === 'exclude' ? 'exclude' : 'include';
    } catch {
      return 'include';
    }
  });
  const changePartialMonths = (value) => {
    setPartialMonths(value);
    try {
      localStorage.setItem('categoriesPartialMonths', value);
    } catch {
      // persistence is best-effort
    }
  };

  const [name, setName] = useState('');
  const [color, setColor] = useState('#3f51b5');
  const [isTransfer, setIsTransfer] = useState(false);
  const [type, setType] = useState('Mixed');
  const [forecastMethod, setForecastMethod] = useState('auto');
  const [editMode, setEditMode] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [formError, setFormError] = useState('');
  const [orderedCategories, setOrderedCategories] = useState([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]); // bulk selection
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryTypeFilter, setCategoryTypeFilter] = useState(''); // '', 'income', 'expense', 'transfer'
  const categoryFiltersScroll = useScrollableFilters();

  // Initialize ordered categories
  useEffect(() => {
    // Sort categories by their 'order' property, or leave them as is if no order
    const sorted = [...categories].sort((a, b) => {
      // Protected categories should always be at the top
      if (['income', 'expense'].includes(a.id)) return -1;
      if (['income', 'expense'].includes(b.id)) return 1;
      
      // Move transfer categories to the bottom
      if (a.isTransfer !== b.isTransfer) {
        return a.isTransfer ? 1 : -1;
      }
      
      // If both have order, sort by that
      if (a.order !== undefined && b.order !== undefined) {
        return a.order - b.order;
      }
      
      // If only one has order, prioritize it
      if (a.order !== undefined) return -1;
      if (b.order !== undefined) return 1;
      
      // Otherwise sort alphabetically
      return a.name.localeCompare(b.name);
    });
    
    setOrderedCategories(sorted);
  }, [categories]);

  const resetForm = () => {
    setName('');
    setColor('#3f51b5');
    setIsTransfer(false);
    setType('Mixed');
    setForecastMethod('auto');
    setEditMode(null);
    setFormError('');
  };

  const openDialog = (category = null) => {
    if (category) {
      setEditMode(category.id);
      setName(category.name);
      setColor(category.color || '#3f51b5');
      setIsTransfer(category.isTransfer || false);
      setType(category.type || 'Mixed');
      setForecastMethod(category.forecastMethod || 'auto');
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = () => {
    if (!name) {
      setFormError('Category name is required');
      return;
    }
    // Generate code from name (lowercase, replace spaces with underscores)
    const generatedId = name.trim().toLowerCase().replace(/\s+/g, '_');
    // Prevent duplicate names
    if (categories.some(cat => cat.name.trim().toLowerCase() === name.trim().toLowerCase() && (!editMode || cat.id !== editMode))) {
      setFormError('A category with this name already exists');
      return;
    }
    const categoryData = {
      id: generatedId,
      name: name.trim(),
      color,
      isTransfer,
      type,
      forecastMethod,
      order: editMode ? 
        categories.find(c => c.id === editMode)?.order : 
        Math.max(0, ...orderedCategories.map(c => c.order || 0)) + 1
    };
    if (editMode) {
      updateCategory({ ...categoryData, originalId: editMode });
    } else {
      addCategory(categoryData);
    }
    closeDialog();
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (categoryToDelete) {
      deleteCategory(categoryToDelete.id);
    }
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
  };

  // Improved card reordering with debouncing
  const moveCard = (dragIndex, hoverIndex) => {
    const dragCategory = orderedCategories[dragIndex];
    
    // Create a new array with the updated order
    const newOrderedCategories = [...orderedCategories];
    newOrderedCategories.splice(dragIndex, 1);
    newOrderedCategories.splice(hoverIndex, 0, dragCategory);
    
    // Update the local state immediately for smooth UI
    setOrderedCategories(newOrderedCategories);
    
    // Clear any existing timeout to prevent multiple rapid saves
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    // Set a new timeout to save changes after a brief delay
    const timeoutId = setTimeout(() => {
      // Calculate new order values for categories that moved
      const updatedCategories = newOrderedCategories.map((category, index) => ({
        ...category,
        order: index
      }));
      
      // Only update categories that have changed position and aren't protected
      updatedCategories.forEach((category, index) => {
        const originalCategory = categories.find(c => c.id === category.id);
        if (originalCategory && 
            originalCategory.order !== index && 
            !['income', 'expense'].includes(category.id)) {
          // Update the category with its new order
          updateCategory({
            ...category,
            originalId: category.id
          });
        }
      });
    }, 500); // Delay of 500ms
    
    setSaveTimeout(timeoutId);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <DragPreviewLayer />
      <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <SectionHeader
          title="Categories"
          icon={<CategoryIcon fontSize="large" />}
        />

      {/* Overflow guard sits below the header so it can't clip the
          full-bleed negative margins */}
      <Box sx={{ overflow: 'hidden' }}>

        <ListSection
          actions={[
            <ToolbarButton key="add" startIcon={<AddIcon />} onClick={() => openDialog()}>
              Add
            </ToolbarButton>,
            // Delete only appears once cards are ticked (via their
            // hover checkboxes), matching the Budgets page.
            ...(selectedCategoryIds.length
              ? [
                  <ToolbarButton
                    key="bulkDelete"
                    startIcon={<DeleteIcon />}
                    onClick={() => setBulkDeleteDialogOpen(true)}
                  >
                    Delete ({selectedCategoryIds.length})
                  </ToolbarButton>
                ]
              : [])
          ]}
          filters={
            <ScrollableFilters
              scrollRef={categoryFiltersScroll.scrollRef}
              showLeftScroll={categoryFiltersScroll.showLeftScroll}
              showRightScroll={categoryFiltersScroll.showRightScroll}
              onScrollLeft={categoryFiltersScroll.scrollLeft}
              onScrollRight={categoryFiltersScroll.scrollRight}
            >
              <TextField
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ minWidth: 200, ...filterFieldSx }}
                InputProps={{
                  startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                  style: { fontSize: '0.875rem' }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 220, ...filterFieldSx }}>
                <InputLabel id="category-filter-type-label" shrink>Type</InputLabel>
                <Select
                  labelId="category-filter-type-label"
                  value={categoryTypeFilter}
                  label="Type"
                  onChange={(e) => setCategoryTypeFilter(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.875rem' }}>All ({categories.length})</MenuItem>
                  <MenuItem value="income" sx={{ fontSize: '0.875rem' }}>Income ({categories.filter(c => c.type === 'Income' || c.id === 'income').length})</MenuItem>
                  <MenuItem value="expense" sx={{ fontSize: '0.875rem' }}>Expense ({categories.filter(c => c.type === 'Expense' || c.id === 'expense').length})</MenuItem>
                  <MenuItem value="transfer" sx={{ fontSize: '0.875rem' }}>Transfer ({categories.filter(c => c.isTransfer).length})</MenuItem>
                </Select>
              </FormControl>
            </ScrollableFilters>
          }
        >
          {/* Card display controls — the history window every card charts,
              and whether the current in-progress month counts toward each
              card's Avg / month (excluding it keeps a half-elapsed month from
              dragging the average down). Sit under the filter pills,
              mirroring the Accounts page's controls. */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 3, rowGap: 1.5, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography variant="body3" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                History
              </Typography>
              <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 6, p: 0.5 }}>
                <Tabs
                  value={RANGE_OPTIONS.findIndex((o) => o.value === range)}
                  onChange={(e, idx) => changeRange(RANGE_OPTIONS[idx].value)}
                  sx={pillTabsSx}
                >
                  {RANGE_OPTIONS.map((o) => (
                    <Tab key={o.value} label={o.label} />
                  ))}
                </Tabs>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography variant="body3" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Partial months
              </Typography>
              <Box sx={{ bgcolor: 'rgba(0, 0, 0, 0.05)', borderRadius: 6, p: 0.5 }}>
                <Tabs
                  value={partialMonths === 'exclude' ? 1 : 0}
                  onChange={(e, idx) => changePartialMonths(idx === 1 ? 'exclude' : 'include')}
                  sx={pillTabsSx}
                >
                  <Tab label="Include" />
                  <Tab label="Exclude" />
                </Tabs>
              </Box>
            </Box>
          </Box>
          {/* Ordering Help Note */}
          {orderedCategories.length > 0 && (
            <InfoBanner sx={{ mt: 2 }}>
              Drag and drop to reorder categories. This order controls how categories appear across the app (lists, selectors & reports).
            </InfoBanner>
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 1.75,
              pt: 2,
              alignItems: 'start',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(auto-fill, minmax(255px, 1fr))',
                md: 'repeat(auto-fill, minmax(290px, 1fr))'
              }
            }}
          >
            {orderedCategories
              .filter(category => {
                const term = searchTerm.trim().toLowerCase();
                if (term && !(category.name || '').toLowerCase().includes(term)) return false;
                if (!categoryTypeFilter) return true;
                if (categoryTypeFilter === 'income') return category.type === 'Income' || category.id === 'income';
                if (categoryTypeFilter === 'expense') return category.type === 'Expense' || category.id === 'expense';
                if (categoryTypeFilter === 'transfer') return category.isTransfer;
                return true;
              })
              .map((category, index) => (
              <CategoryCard
                key={category.id}
                category={category}
                index={index}
                monthKeys={monthKeys}
                range={range}
                budgetData={budgetData}
                budgets={budgets}
                transactions={transactions}
                includePartialMonth={partialMonths === 'include'}
                onEdit={() => openDialog(category)}
                onDelete={handleDeleteClick}
                onAnalyze={setAnalyzeCategory}
                moveCard={moveCard}
                selectable
                selected={selectedCategoryIds.includes(category.id)}
                onToggleSelect={(id) => setSelectedCategoryIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
              />
            ))}
            {orderedCategories.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No categories found
              </Typography>
            )}
          </Box>
        </ListSection>

        {/* Add/Edit Category Dialog */}
        <AppDialog
          open={dialogOpen}
          onClose={closeDialog}
          title={editMode ? 'Edit Category' : 'Add Category'}
          subtitle="Organize transactions for budgets & reports"
          primaryLabel={editMode ? 'Save' : 'Add'}
          onPrimary={handleSubmit}
        >
          {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
          <DialogField label="Name" required>
            <TextField
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              placeholder="e.g. Groceries"
              autoFocus
            />
          </DialogField>
          <DialogField label="Type">
            <FormControl fullWidth size="small">
              <Select
                labelId="type-select-label"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <MenuItem value="Income">Income</MenuItem>
                <MenuItem value="Expense">Expense</MenuItem>
                <MenuItem value="Mixed">Mixed</MenuItem>
              </Select>
            </FormControl>
          </DialogField>
          <DialogField
            label="Forecast method"
            helper={FORECAST_METHODS.find((m) => m.value === forecastMethod)?.description}
          >
            <FormControl fullWidth size="small">
              <Select
                value={forecastMethod}
                onChange={(e) => setForecastMethod(e.target.value)}
              >
                {FORECAST_METHODS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogField>
          <DialogField label="Color">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: color, border: '2px solid', borderColor: 'grey.200' }} />
              <Typography variant="caption" color="text.secondary">{color}</Typography>
            </Box>
            <ChromePicker
              color={color}
              onChange={c => setColor(c.hex)}
              disableAlpha
              styles={{
                default: {
                  picker: {
                    boxShadow: 'none',
                    borderRadius: 12,
                    fontFamily: 'Inter, sans-serif',
                    width: '100%'
                  },
                  input: { fontFamily: 'Inter, sans-serif' },
                  label: { fontFamily: 'Inter, sans-serif', fontWeight: 500 }
                }
              }}
            />
          </DialogField>
          <DialogField helper="Transfers move money between accounts and are not counted as income or expense.">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isTransfer}
                  onChange={(e) => setIsTransfer(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Mark as transfer</Typography>}
            />
          </DialogField>
        </AppDialog>
        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Category"
          message={`Are you sure you want to delete the category "${categoryToDelete?.name}"? This will affect all transactions using this category.`}
          confirmLabel="Delete"
          onConfirm={confirmDelete}
        />
        {/* AI Category Analysis Dialog */}
        <CategoryAnalysisDialog
          open={!!analyzeCategory}
          onClose={() => setAnalyzeCategory(null)}
          category={analyzeCategory}
          budgetData={budgetData}
          transactions={transactions}
        />
        {/* Bulk Delete Confirmation Dialog */}
        <ConfirmDialog
          open={bulkDeleteDialogOpen}
          onClose={() => setBulkDeleteDialogOpen(false)}
          title={`Delete ${selectedCategoryIds.length} categor${selectedCategoryIds.length === 1 ? 'y' : 'ies'}?`}
          message="This action cannot be undone."
          confirmLabel="Delete"
          onConfirm={() => {
            selectedCategoryIds.forEach(id => deleteCategory(id));
            setSelectedCategoryIds([]);
            setBulkDeleteDialogOpen(false);
          }}
        />
      </Box>
      </Box>
    </DndProvider>
  );
};

// ...existing code...
export default CategoryManager;